import {
	Box,
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	Grid,
	IconButton,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { API_DATE_TIME_FORMAT, isTime } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	AwardType,
	Condition,
	CountUnit,
	DialogAssignPage,
	RegularLoopType,
} from '@/api/enum/common.enum';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AddRewardItemDialog from './AddRewardItemDialog';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import {
	useCreateRegularMutation,
	useUpdateRegularMutation,
} from '@/api/task/regularApiSlice';
import AddExerciseDialog from './AddExerciseDialog';
import AddSeriesDialog from './AddSeriesDialog';
import {
	useCreateActivitySingleDetailMutation,
	useUpdateActivitySingleDetailMutation,
} from '@/api/task/activityApiSlice';
import { TaskActivitySingleDTO } from '@/api/DTO/task/activity.interface';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppDateTimePicker from '@/common/components/form-inputs/AppDateTimePicker';
import { TASK_LIST_PAGE } from '@/common/constants/common';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

interface RewardItem {
	rewardId: string;
	name: string;
	count: string;
	[key: string]: any;
}

export default function RegularDayInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const { activityId, activitySingleId } = useParams();
	const [createActivitySingleDetail] = useCreateActivitySingleDetailMutation();
	const [updateActivitySingleDetail] = useUpdateActivitySingleDetailMutation();

	const conditionList = [
		{ id: Condition.OpenApp, name: t('taskManage.OpenApp') },
		// { id: Condition.Online, name: t('taskManage.Online') },
		{ id: Condition.Topage, name: t('taskManage.Topage') },
		{ id: Condition.Exercise, name: t('taskManage.Exercise') },
		{
			id: Condition.AssignAllExercise,
			name: t('taskManage.AssignAllExercise'),
		},
		{ id: Condition.NewExercise, name: t('taskManage.NewExercise') },
		{ id: Condition.Health, name: t('taskManage.Health') },
		{ id: Condition.ExerciseClock, name: t('taskManage.ExerciseClock') },
		{ id: Condition.MajorExercise, name: t('taskManage.MajorExercise') },
		{ id: Condition.HeartExercise, name: t('taskManage.HeartExercise') },
		{
			id: Condition.CrossoverExercise,
			name: t('taskManage.CrossoverExercise'),
		},
		{ id: Condition.BodyExercise, name: t('taskManage.BodyExercise') },
		{
			id: Condition.MeditationExercise,
			name: t('taskManage.MeditationExercise'),
		},
		{ id: Condition.Day21Exercise, name: t('taskManage.Day21Exercise') },
		{ id: Condition.AllExercise, name: t('taskManage.AllExercise') },
		{ id: Condition.ChangeEmail, name: t('taskManage.ChangeEmail') },
		{ id: Condition.ChangePhone, name: t('taskManage.ChangePhone') },
		{ id: Condition.ChangeNickName, name: t('taskManage.ChangeNickName') },
		{ id: Condition.ChangeProfile, name: t('taskManage.ChangeProfile') },
	];
	const awardTypeList = [
		{ id: AwardType.Int, name: t('taskManage.Int') },
		{ id: AwardType.Prop, name: t('taskManage.Prop') },
		{ id: AwardType.IntProp, name: t('taskManage.IntProp') },
	];
	const cycleTypeList = [
		{ id: RegularLoopType.Day, name: t('regularTask.day') },
		{ id: RegularLoopType.Week, name: t('regularTask.week') },
		{ id: RegularLoopType.Month, name: t('regularTask.month') },
	];

	const defaultValues: DefaultValues<TaskActivitySingleDTO> = {};
	useEffect(() => {
		if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
		if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
		if (details?.descriptionZh)
			methods.setValue('titleEn', details.descriptionZh);
		if (details?.descriptionEn)
			methods.setValue('descriptionEn', details.descriptionEn);
		if (details?.publishedEndAt)
			methods.setValue('publishedEndAt', details.publishedEndAt);
		if (details?.publishedStartAt)
			methods.setValue('publishedStartAt', details.publishedStartAt);

		if (details?.isCycle) {
			methods.setValue('isCycle', true);
		} else {
			methods.setValue('isCycle', false);
		}

		if (details?.cycleType > -1)
			methods.setValue('cycleType', details.cycleType);

		if (details?.finishedCondition > -1)
			methods.setValue('finishedCondition', details.finishedCondition);
		if (details?.finishedConditionUnit > -1)
			methods.setValue('finishedConditionUnit', details.finishedConditionUnit);
		if (details?.rewardType > -1)
			methods.setValue('rewardType', details.rewardType);
		if (details?.finishedNum > -1)
			methods.setValue('finishedNum', details.finishedNum);
		if (details?.rewardPoints > -1)
			methods.setValue('rewardPoints', details.rewardPoints);
		if (details?.rewardList) {
			const arr = details.rewardList.map((item: any) => {
				return {
					...item,
					titleZh: item.seriesTitleZh + ' - ' + item.titleZh,
				};
			});
			methods.setValue('rewardList', arr);

			optionsReplace(arr);
		}

		if (details?.pageIndex > -1)
			methods.setValue('pageIndex', details.pageIndex);

		if (details?.exerciseId) {
			methods.setValue('exerciseId', details.exerciseId);
		}
		if (details?.exerciseName) {
			methods.setValue('exerciseName', details.exerciseName);
		}
		if (details?.exerciseSeriesId) {
			methods.setValue('exerciseSeriesId', details.exerciseSeriesId);
		}
		if (details?.exerciseSeriesName) {
			methods.setValue('exerciseSeriesName', details.exerciseSeriesName);
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		publishedStartAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		publishedEndAt: Yup.string()
			.nullable()
			.test(
				'is-valid-iso-date',
				t('errorMessage.pleaseSelectTime'),
				(value) => {
					return isTime(value);
				}
			),
		titleZh: Yup.string().required(t('errorMessage.required')),
		isCycle: Yup.boolean().required(t('errorMessage.required')),
		cycleType: Yup.string()
			.nullable()
			.when('isCycle', {
				is: (isCycle: boolean) => isCycle,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
		finishedCondition: Yup.string().required(t('errorMessage.pleaseSelect')),
		finishedConditionUnit: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		finishedNum: Yup.string().required(t('errorMessage.required')),
		rewardType: Yup.string().required(t('errorMessage.pleaseSelect')),

		pageIndex: Yup.string()
			.nullable()
			.when('finishedCondition', {
				is: (finishedCondition: string | number) =>
					finishedCondition == Condition.Topage,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		rewardPoints: Yup.string()
			.nullable()
			.when('rewardType', {
				is: (rewardType: string | number) =>
					rewardType == AwardType.Int || rewardType == AwardType.IntProp,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		exerciseId: Yup.string()
			.nullable()
			.when('finishedCondition', {
				is: (finishedCondition: string | number) =>
					finishedCondition == Condition.Exercise,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		exerciseSeriesId: Yup.string()
			.nullable()
			.when('finishedCondition', {
				is: (finishedCondition: string | number) =>
					finishedCondition == Condition.AssignAllExercise,
				then: (schema) => schema.required(t('errorMessage.required')),
			}),
		rewardList: Yup.array()
			.nullable()
			.when('rewardType', {
				is: (rewardType: string | number) =>
					rewardType == AwardType.Prop || rewardType == AwardType.IntProp,
				then: (schema) =>
					schema
						.required(t('errorMessage.pleaseSelect'))
						.min(1, t('errorMessage.pleaseSelect')),
				otherwise: (schema) => schema.nullable().notRequired(),
			}),
	});
	const methods = useForm<TaskActivitySingleDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const watchFinishedCondition = methods.watch('finishedCondition');
	const watchRewardType = methods.watch('rewardType');
	const watchExerciseId = methods.watch('exerciseId');
	const watchExerciseSeriesId = methods.watch('exerciseSeriesId');
	const watchIsCycle = methods.watch('isCycle');

	const {
		fields: rewardListField,
		append: optionsAppend,
		remove: optionsRemove,
		update: optionsUpdate,
		replace: optionsReplace,
	}: any = useFieldArray({
		control: methods.control,
		name: 'rewardList',
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<TaskActivitySingleDTO> = async (data) => {
		const obj = { ...data, isCycle: data.isCycle ? 1 : 0 };
		obj.rewardList = methods.getValues('rewardList')?.map((item) => {
			delete item.id;
			return {
				...item,
			};
		});
		console.log(obj);
		if (!data.isCycle) {
			delete obj.cycleType;
		}
		if (data.rewardType == AwardType.Int) {
			delete obj.rewardList;
		}
		if (data.rewardType == AwardType.Prop) {
			delete obj.rewardPoints;
		}
		if (data.finishedCondition != Condition.Exercise) {
			delete obj.exerciseId;
			delete obj.exerciseName;
		}
		if (data.finishedCondition != Condition.AssignAllExercise) {
			delete obj.exerciseSeriesId;
			delete obj.exerciseSeriesName;
		}
		console.log(obj);
		const response =
			type == FormType.create
				? await createActivitySingleDetail({ ...obj, id: activityId || '' })
				: await updateActivitySingleDetail({
						...obj,
						id: activitySingleId || '',
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.addTaskSuccess')
					: t('snackbar.changeContentSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/task-manage/activity-list/info/${activityId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/task-manage/activity-list/info/${activityId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleGetRewardList = (list: RewardItem[]) => {
		if (list.length > 0) {
			const arr = methods.getValues('rewardList') || [];
			arr.push(list[0]);
			optionsReplace(arr);
		}
	};

	const handleDeleteReward = (index: number) => {
		optionsRemove(index);
	};

	const handleGetExercise = (arr: any) => {
		if (arr.length > 0) {
			methods.setValue('exerciseId', arr[0].exerciseId);
			methods.setValue('exerciseName', arr[0].titleZh);
		}
	};

	const handleDeleteExercise = () => {
		methods.setValue('exerciseId', '');
		methods.setValue('exerciseName', '');
	};
	const handleGetSeries = (arr: any) => {
		console.log(arr);
		if (arr.length > 0) {
			methods.setValue('exerciseSeriesId', arr[0].seriesId);
			methods.setValue('exerciseSeriesName', arr[0].seriesTitleZh);
		}
	};

	const handleDeleteSeries = () => {
		methods.setValue('exerciseSeriesId', '');
		methods.setValue('exerciseSeriesName', '');
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>

						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('taskManage.taskName')}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionZh'
											multiline
											label={t('taskManage.taskDescription_')}
											maxLength={500}
											minRows={4}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('taskManage.taskName')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionEn'
											multiline
											label={t('taskManage.taskDescription_')}
											maxLength={500}
											minRows={4}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedStartAt'
									label={t('taskManage.pushedTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={6}>
								<AppDateTimePicker
									minimumDate={dayjs()
										.add(0, 'day')
										.format(API_DATE_TIME_FORMAT)}
									control={methods.control}
									name='publishedEndAt'
									label={t('taskManage.pulledTime_')}
									formatDate={API_DATE_TIME_FORMAT}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{t('taskManage.createActivitySingleTips')}
									</Typography>
								</Box>
							</Grid>
							<Grid item xs={6}>
								<SingleCheckbox
									control={methods.control}
									name='isCycle'
									label={t('taskManage.loopTask')}
								/>
							</Grid>
							{watchIsCycle && (
								<Grid item xs={6}>
									<AppRadioGroup
										control={methods.control}
										name='cycleType'
										row
										options={cycleTypeList}
									/>
								</Grid>
							)}
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid item xs={12}>
								<AppDropDownMenu
									disabled={type == FormType.edit}
									control={methods.control}
									name='finishedCondition'
									label={t('taskManage.completionConditionsMore')}
									options={conditionList}
								/>
							</Grid>
							{Number(watchFinishedCondition) > -1 && (
								<>
									{[Condition.Topage].includes(
										Number(watchFinishedCondition)
									) && (
										<>
											<Grid item xs={12}>
												<AppDropDownMenu
													control={methods.control}
													name='pageIndex'
													label={t('dialogList.showPage')}
													options={TASK_LIST_PAGE}
												/>
											</Grid>
										</>
									)}
									{[Condition.Exercise].includes(
										Number(watchFinishedCondition)
									) && (
										<>
											{watchExerciseId ? (
												<Grid item xs={6.1} sx={{ display: 'flex' }}>
													<AppTextField
														control={methods.control}
														name='exerciseName'
														label={t('taskManage.designatedExercise')}
														disabled
													/>
													<IconButton
														aria-label='close'
														onClick={() => handleDeleteExercise()}
														color='error'
													>
														<DeleteOutlineOutlinedIcon />
													</IconButton>
												</Grid>
											) : (
												<Grid item xs={12}>
													<AddExerciseDialog
														handleGetExercise={handleGetExercise}
													/>
													<FormHelperText style={{ color: '#d32f2f' }}>
														{typeof methods.formState.errors.exerciseId
															?.message === 'string'
															? methods.formState.errors.exerciseId.message
															: ''}
													</FormHelperText>
												</Grid>
											)}
										</>
									)}
									{[Condition.AssignAllExercise].includes(
										Number(watchFinishedCondition)
									) && (
										<>
											{watchExerciseSeriesId ? (
												<Grid item xs={6.1} sx={{ display: 'flex' }}>
													<AppTextField
														control={methods.control}
														name='exerciseSeriesName'
														label={t('taskManage.designatedSeries')}
														disabled
													/>
													<IconButton
														aria-label='close'
														onClick={() => handleDeleteSeries()}
														color='error'
													>
														<DeleteOutlineOutlinedIcon />
													</IconButton>
												</Grid>
											) : (
												<Grid item xs={12}>
													<AddSeriesDialog handleGetSeries={handleGetSeries} />
													<FormHelperText style={{ color: '#d32f2f' }}>
														{typeof methods.formState.errors.exerciseSeriesId
															?.message === 'string'
															? methods.formState.errors.exerciseSeriesId
																	.message
															: ''}
													</FormHelperText>
												</Grid>
											)}
										</>
									)}
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('taskManage.quantityUnit')}
										</Typography>
										{[
											Condition.OpenApp,
											Condition.Topage,
											Condition.Health,
											Condition.ChangeEmail,
											Condition.ChangePhone,
											Condition.ChangeNickName,
											Condition.ChangeProfile,
										].includes(Number(watchFinishedCondition)) && (
											<AppRadioGroup
												control={methods.control}
												name='finishedConditionUnit'
												row
												options={[
													{ id: CountUnit.Time, name: t('taskManage.Time') },
												]}
											/>
										)}
										{/* {[Condition.Online].includes(
											Number(watchFinishedCondition)
										) && (
											<AppRadioGroup
												control={methods.control}
												name='finishedConditionUnit'
												row
												options={[
													{
														id: CountUnit.Minute,
														name: t('taskManage.Minute'),
													},
												]}
											/>
										)} */}
										{[
											Condition.Exercise,
											Condition.AssignAllExercise,
											Condition.ExerciseClock,
											Condition.HeartExercise,
										].includes(Number(watchFinishedCondition)) && (
											<AppRadioGroup
												control={methods.control}
												name='finishedConditionUnit'
												row
												options={[
													{ id: CountUnit.Time, name: t('taskManage.Time') },
													{
														id: CountUnit.Minute,
														name: t('taskManage.Minute'),
													},
												]}
											/>
										)}
										{[
											Condition.NewExercise,
											Condition.MajorExercise,
											Condition.CrossoverExercise,
											Condition.BodyExercise,
											Condition.MeditationExercise,
											Condition.Day21Exercise,
											Condition.AllExercise,
										].includes(Number(watchFinishedCondition)) && (
											<AppRadioGroup
												control={methods.control}
												name='finishedConditionUnit'
												row
												options={[
													{ id: CountUnit.Time, name: t('taskManage.Time') },
													{
														id: CountUnit.Minute,
														name: t('taskManage.Minute'),
													},
													{ id: CountUnit.Diff, name: t('taskManage.Diff') },
												]}
											/>
										)}
									</Grid>
									<Grid item xs={6}>
										<AppTextField
											inputType={InputType.number}
											control={methods.control}
											name='finishedNum'
											label={t('taskManage.total')}
										/>
									</Grid>
									<Grid item xs={6}>
										<Typography variant='body2' color='common.neutral40'>
											{t('taskManage.awardType')}
										</Typography>
										<AppRadioGroup
											control={methods.control}
											name='rewardType'
											row
											options={awardTypeList}
										/>
									</Grid>
									{[AwardType.Int, AwardType.IntProp].includes(
										Number(watchRewardType)
									) && (
										<Grid item xs={6}>
											<AppTextField
												control={methods.control}
												name='rewardPoints'
												label={t('taskManage.rewardPoints')}
												inputType={InputType.number}
												maxLength={5}
											/>
										</Grid>
									)}
									{[AwardType.Prop, AwardType.IntProp].includes(
										Number(watchRewardType)
									) &&
										rewardListField.map((item: any, index: number) => (
											<React.Fragment key={item.rewardId}>
												{index == 0 && watchRewardType == AwardType.Prop && (
													<Grid item xs={6}></Grid>
												)}
												<Grid item xs={6}>
													<AppTextField
														disabled
														control={methods.control}
														name={`rewardList.${index}.titleZh`}
														label={t('regularTask.rewardItem')}
													/>
												</Grid>
												<Grid item xs={6} sx={{ display: 'flex' }}>
													<AppTextField
														control={methods.control}
														name={`rewardList.${index}.rewardNum`}
														label={t('regularTask.rewardItemCount')}
														inputType={InputType.number}
													/>
													<IconButton
														aria-label='close'
														onClick={() => handleDeleteReward(index)}
														color='error'
													>
														<DeleteOutlineOutlinedIcon />
													</IconButton>
												</Grid>
											</React.Fragment>
										))}
									{[AwardType.Prop, AwardType.IntProp].includes(
										Number(watchRewardType)
									) && (
										<Grid item xs={12}>
											<AddRewardItemDialog
												handleGetRewardList={handleGetRewardList}
											/>
											<FormHelperText style={{ color: '#d32f2f' }}>
												{typeof methods.formState.errors.rewardList?.message ===
												'string'
													? methods.formState.errors.rewardList.message
													: ''}
											</FormHelperText>
										</Grid>
									)}
								</>
							)}
						</Grid>
						<Grid container spacing={3}></Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
